<template>
  <div>
    <dl
      v-for="(item, i) in displayItems"
      :key="i"
      class="boss-resumecard-any-list"
    >
      <dt class="boss-resumecard-any-head">
        {{ item.name }}
      </dt>
      <dd
        :data-test="`member-${item.key}`"
        class="boss-resumecard-any-body"
      >
        {{ item.data() | parseLabel(item.name) }}
      </dd>
    </dl>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import * as definition from '@/lib/labelValueMappings'
import dayjs from 'dayjs'
import _ from 'lodash'

export default {
  filters: {
    parseLabel (val) {
      return val || '未登録'
    }
  },
  props: {
    member: {
      type: Object,
      required: true
    },
    comment: {
      type: String,
      required: true
    }
  },
  data () {
    return {
      itemMap: [
        {
          key: 'experienced_occupations',
          data: () => this.convertSliceObjectLabel(this.member.experiencedOccupations, 0, 2),
          name: 'その他の経験職種'
        }, {
          key: 'educational_background',
          data: () => this.educationalBackground,
          name: '最終学歴'
        }, {
          key: 'graduated_school',
          data: () => this.attribute('graduatedSchool.name'),
          name: '最終学歴（学校名）'
        }, {
          key: 'graduated_school_detail',
          data: () => this.attribute('graduatedSchool.department'),
          name: '最終学歴（学部/学科/専攻）'
        }, {
          key: 'department_code',
          data: () => this.attribute('graduatedEducationDepartment.name'),
          name: '学部学科コード'
        }, {
          key: 'experienced_businesses',
          data: () => this.convertSliceArrayLabel(this.member.experiencedBusinesses, 0, 2),
          name: '経験業種'
        }, {
          key: 'desired_businesses',
          data: () => this.convertSliceArrayLabel(this.member.desiredBusinesses, 0, 2),
          name: '希望業種'
        }, {
          key: 'toeic_toefl_score',
          data: () => this.convertToeicAndToefl(this.member.toeicScore, this.member.toeflScore),
          name: 'TOEIC・TOEFL'
        }, {
          key: 'english_skill',
          data: () => this.attribute('englishSkill.name'),
          name: '英語スキル'
        }, {
          key: 'languages_other_than_english',
          data: () => this.languagesOtherThanEnglishLabel,
          name: '英語以外の語学スキル'
        }, {
          key: 'licenses',
          data: () => this.createLicensesLabel(this.member.otherLicense, this.member.licenses),
          name: '資格'
        }, {
          key: 'skill',
          data: () => this.skillLabel,
          name: 'スキル'
        }, {
          key: 'company_name',
          data: () => this.latestCompany,
          name: '直近の社名'
        }, {
          key: 'working_period',
          data: () => this.latestCompanyPeriod,
          name: '直近の勤務期間'
        }, {
          key: 'business_content',
          data: () => this.attribute('careerResume.descriptionTxt1'),
          name: '直近の事業内容'
        }, {
          key: 'work_content',
          data: () => this.attribute('careerResume.descriptionTxt2'),
          name: '直近の仕事内容'
        }, {
          key: 'achievement_experience',
          data: () => this.attribute('careerResume.descriptionTxt3'),
          name: '直近の成果・経験'
        }, {
          key: 'other_company_names',
          data: () => this.attribute('otherCompanies') ? this.attribute('otherCompanies').join('、') : '',
          name: 'その他の社名'
        }, {
          key: 'recent_employment_status',
          data: () => this.attribute('recentEmploy.name'),
          name: '直近の雇用形態'
        }, {
          key: 'desired_employment_status',
          data: () => this.desiredEmployLabel,
          name: '希望の雇用形態'
        }, {
          key: 'married',
          data: () => this.attribute('marriage'),
          name: '未既婚'
        }, {
          key: 'has_children',
          data: () => this.attribute('childStatus'),
          name: '子供有無'
        }, {
          key: 'change_jobs',
          data: () => this.attribute('homeMoving'),
          name: '転居を伴う転職可否'
        }, {
          key: 'self_pr',
          data: () => this.attribute('publicRelations'),
          name: '自己PR'
        }, {
          key: 'comment',
          data: () => this.comment,
          name: 'コメント'
        }
      ]
    }
  },
  computed: {
    ...mapGetters('settings', [
      'scoutDisplayItems'
    ]),
    latestCompany () {
      const publicationCompanyName = this.attribute('careerResume.publicationCompanyName')
      if (_.isBoolean(publicationCompanyName) && !publicationCompanyName) {
        return '社名非公開'
      }
      const companyName = this.attribute('careerResume.companyName')
      const employmentName = this.attribute('careerResume.employmentPattern.name')
      if (companyName && employmentName) {
        return `${companyName}[${employmentName}]`
      } else if (companyName) {
        return `${companyName}[雇用形態未登録]`
      } else {
        return ''
      }
    },
    latestCompanyPeriod () {
      const firstYearMonthArray = (this.attribute('careerResume.firstYearMonth') || '').split('-')
      const lastYearMonthArray = (this.attribute('careerResume.lastYearMonth') || '').split('-')
      // 入社年月および退社年月が未設定＝直近の勤務期間未登録と判断する。
      if (firstYearMonthArray.length !== 2 && lastYearMonthArray.length !== 2) {
        return ''
      }
      return `${firstYearMonthArray.length === 2 ? `${firstYearMonthArray[0]}年${firstYearMonthArray[1]}月〜` : ''}${lastYearMonthArray.length === 2 ? `${lastYearMonthArray[0]}年${lastYearMonthArray[1]}月` : '現在就業中'}`
    },
    attribute () {
      return (key) => {
        return _.get(this.member, key, '')
      }
    },
    educationalBackground () {
      return this.attribute('educationalBackground.name') + this.parseDate(this.attribute('graduatedSchool.yearMonth'))
    },
    displayItems () {
      return _.map(this.filteredDisplayItems, v => {
        return _.find(this.itemMap, o => o.key === v)
      })
    },
    filteredDisplayItems () {
      return (this.comment) ? this.scoutDisplayItems : _.filter(this.scoutDisplayItems, v => v !== 'comment')
    },
    skillLabel () {
      const skills = this.attribute('skills')
      if (!skills || !skills.length) return ''
      return skills.map(skill => {
        let skillText = skill.name
        const skillLevel = definition.registeredExperienceRange.find(item => item.level === skill.level)
        if (skillLevel) {
          skillText += `（${skillLevel.name}）`
        }
        return skillText
      }).join('、')
    },
    desiredEmployLabel () {
      return this.convertArrayLabel(this.attribute('desiredEmploy'))
    },
    languagesOtherThanEnglishLabel () {
      const languagesOtherThanEnglishes = this.attribute('languagesOtherThanEnglish')
      if (!languagesOtherThanEnglishes || !languagesOtherThanEnglishes.length) return ''
      return languagesOtherThanEnglishes.map(languagesOtherThanEnglish =>
        `${languagesOtherThanEnglish.name.name}(${languagesOtherThanEnglish.level.name})`
      ).join('、')
    }
  },
  methods: {
    convertSliceArrayLabel (value, begin, end) {
      if (!value) return ''
      return value.slice(begin, end).map(v => _.get(v, 'name')).join('、')
    },
    convertArrayLabel (value) {
      if (!value || !value.length) return ''
      return value.map(v => _.get(v, 'name')).join('、')
    },
    createLicensesLabel (otherLicense, licenses) {
      const otherLicenseLabel = otherLicense || ''
      const licenseNamesLabel = (licenses || []).map(license => license.name)
      const label = _.compact([...licenseNamesLabel, otherLicenseLabel]).join('、')
      if (!label) return '保有資格なし'
      return label.length >= 50 ? label.substr(0, 50) + '...' : label
    },
    convertSliceObjectLabel (value, begin, end) {
      if (!value || !value.length) return ''
      return value.slice(begin, end).map(v => {
        return _.get(v, 'name')
      }).join('、')
    },
    convertToeicAndToefl (toeicScore, toeflScore) {
      if (toeicScore && toeflScore) {
        return `TOEIC:${toeicScore}点、TOEFL:${toeflScore}点`
      } else if (toeicScore) {
        return `TOEIC:${toeicScore}点、TOEFL:未登録`
      } else if (toeflScore) {
        return `TOEIC:未登録、TOEFL:${toeflScore}点`
      } else {
        return ''
      }
    },
    parseDate (yearMonth) {
      return yearMonth ? '(' + dayjs(Date.parse(`${yearMonth}-01`)).format('YYYY年M月') + ')' : ''
    }
  }
}
</script>
